// src/pages/CompleteProfile.jsx
// 3.62.26 (owner): newly added people check their name and add their phone
// number and extension at first sign-in, after choosing their password and
// setting up two-factor authentication, before the portal opens. Shown once
// (askProfile from /portal/user-info); saving sets profileConfirmedAt.
// Phone and extension stay optional, as in My profile.
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import AuthShell, { A, AuthMessage } from '../components/AuthShell';
import { updateMe } from '../utils/api';
import { phone as checkPhone, display as showPhone, PHONE_HELP } from '../utils/phone';

export default function CompleteProfile() {
  const { user, userInfo, logout, getToken, updateUserInfo } = useAuth();
  const [name, setName]   = useState(userInfo?.name && userInfo.name !== user?.email ? userInfo.name : '');
  const [phone, setPhone] = useState(showPhone(userInfo?.phone));
  const [ext, setExt]     = useState(userInfo?.phoneExt || '');
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');

  async function submit(e) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) { setErr('Please enter your name.'); return; }
    const typed = checkPhone(phone);
    if (!typed.ok) { setErr(`That isn't a valid phone number. ${PHONE_HELP}`); return; }
    setBusy(true);
    try {
      const saved = await updateMe({ name: name.trim(), phone: typed.e164, phoneExt: ext.trim(), profileConfirmed: true }, getToken);
      updateUserInfo({ name: saved.name, phone: saved.phone, phoneExt: saved.phoneExt || '', askProfile: false });
    } catch (e2) {
      setErr(e2.message || "Couldn't save. Please try again.");
      setBusy(false);
    }
  }

  return (
    <AuthShell stack>
      <h1 style={S.title}>Your profile</h1>
      <p style={S.text}>Please check your name and add your phone number and extension. Your name and extension print on your fax cover sheets. You can change them any time in <strong>My profile</strong>.</p>
      <form onSubmit={submit} style={S.form}>
        <label style={S.label}>Name
          <input value={name} onChange={e => setName(e.target.value)} maxLength={80} required autoFocus
            autoComplete="name" style={S.input} />
        </label>
        <div style={S.phoneRow}>
          <label style={{ ...S.label, flex: 1, minWidth: 0 }}><span>Phone number <span style={S.optional}>(optional)</span></span>
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              onBlur={() => { const p = checkPhone(phone); if (p.ok && p.display) setPhone(p.display); }}
              autoComplete="tel" placeholder="e.g. (800) 766-8732" style={S.input} />
          </label>
          <label style={{ ...S.label, width: 96, flexShrink: 0 }}><span>Ext. <span style={S.optional}>(optional)</span></span>
            <input value={ext} onChange={e => setExt(e.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric"
              placeholder="123" aria-label="Phone extension (optional)" style={S.input} />
          </label>
        </div>
        {err && <AuthMessage tone="error">{err}</AuthMessage>}
        <button type="submit" disabled={busy} className="auth-btn">{busy ? 'Saving…' : 'Save and continue'}</button>
      </form>
      <div style={S.footerRow}>
        <span style={S.signedInAs}>Signed in as {user?.email}</span>
        <button onClick={logout} style={S.link} className="auth-link">Sign out</button>
      </div>
    </AuthShell>
  );
}


const S = {  // follows ChooseOwnPassword (AuthShell look, A)
  title: { ...A.title, margin: 0 },
  text: { ...A.text, margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, ...A.label },
  optional: { fontWeight: 500, color: '#90A4AE' },
  phoneRow: { display: 'flex', gap: 10, alignItems: 'flex-end' },
  input: { ...A.input, marginBottom: 0 },
  footerRow: { ...A.footerRow, marginTop: 6 },
  signedInAs: A.signedInAs,
  link: A.link,
};
